import produce from 'immer';
import { ActionType, getType } from 'typesafe-actions';
import { RequestStatus } from '../../types';
import * as actions from './actions';
import { IImportedMediaItem } from './types';

type MediaImportActions = ActionType<typeof actions>;

interface IImportSource<T> {
  url?: string;
  status: RequestStatus;
  items?: T;
  error?: Error | string;
}

interface IMediaImportState {
  facebook: IImportSource<IImportedMediaItem[]>;
  site: IImportSource<IImportedMediaItem[]>;
  selection: string[];
  showImportNotification: boolean;
}

const INITIAL_STATE: IMediaImportState = {
  facebook: { status: RequestStatus.INITIAL },
  site: { status: RequestStatus.INITIAL },
  selection: [],
  showImportNotification: false,
};

function reducer(
  state: IMediaImportState = INITIAL_STATE,
  action: MediaImportActions,
): IMediaImportState {
  return produce(state, (draftState) => {
    switch (action.type) {
      case getType(actions.importFacebook.request):
        draftState.facebook.url = action.payload;
        delete draftState.facebook.error;
        draftState.facebook.status = RequestStatus.REQUEST;
        break;

      case getType(actions.importFacebook.success):
        draftState.facebook.items = action.payload;
        draftState.facebook.status = RequestStatus.SUCCESS;
        break;

      case getType(actions.importFacebook.failure):
        draftState.facebook.error = action.payload;
        draftState.facebook.status = RequestStatus.FAILURE;
        break;

      case getType(actions.storeFacebookUrl):
        draftState.facebook.url = action.payload;
        break;

      case getType(actions.importSite.request):
        delete draftState.site.error;
        draftState.site.status = RequestStatus.REQUEST;
        break;

      case getType(actions.importSite.success):
        draftState.site.items = action.payload;
        draftState.site.status = RequestStatus.SUCCESS;
        break;

      case getType(actions.importSite.failure):
        draftState.site.error = action.payload;
        draftState.site.status = RequestStatus.FAILURE;
        break;

      case getType(actions.storeSiteUrl):
        draftState.site.url = action.payload;
        break;

      case getType(actions.clearFacebookMedia):
        if (
          state.facebook.status === RequestStatus.SUCCESS &&
          state.selection.length
        ) {
          draftState.selection = excludeSelection(
            state.selection,
            state.facebook.items,
          );
        }
        draftState.facebook = INITIAL_STATE.facebook;
        break;

      case getType(actions.clearSiteMedia):
        if (
          state.site.status === RequestStatus.SUCCESS &&
          state.selection.length
        ) {
          draftState.selection = excludeSelection(
            state.selection,
            state.site.items,
          );
        }
        draftState.site = INITIAL_STATE.site;
        break;

      case getType(actions.toggleSelected):
        const url = action.payload.url;
        if (action.payload.selected) {
          if (!state.selection.includes(url)) {
            draftState.selection.push(url);
          }
        } else {
          draftState.selection = state.selection.filter((item) => item !== url);
        }
        break;

      case getType(actions.preselectImportedMedia):
        draftState.selection = action.payload;
        break;

      case getType(actions.toggleImportNotification):
        draftState.showImportNotification = action.payload;
        break;

      default:
    }
  });
}

function excludeSelection(
  selection: string[],
  items: IImportedMediaItem[],
): string[] {
  const urls = items.map((i) => i.url);
  return selection.filter((s) => !urls.includes(s));
}

export default reducer;
export { MediaImportActions, IMediaImportState, IImportSource };
